import Server from '../../api/server'
import {
  EMAIL_USERS_ROUTE,
  UNENROLL_DEVICES_ROUTE,
  UNENROLLED_DEVICE_ROUTE,
  INSECURE_DEVICE_ROUTE,
  DASHBOARD_STATS_ROUTE,
  UPDATE_DEVICE_NAME_ROUTE,
  DASHBOARD_DEVICES_CREATED_STAT_ROUTE,
  DASHBOARD_ALERTS_CREATED_STAT_ROUTE,
  DASHBOARD_TOTAL_SCANS_STAT_ROUTE,
  DASHBOARD_INSECURE_DEVICES_STAT_ROUTE,
  DEVICE_DETAILS_ROUTE,
  API_URL,
  ENROLLED_DEVICE_ROUTE,
  DEVICE_SCANS_ROUTE,
  DEVICE_ALERTS_ROUTE,
  DEVICE_EVENTS_ROUTE,
  DEVICE_FORENSICS_ROUTE,
  DEVICE_FILE_EVENTS_ROUTE,
  SUPPORTED_IOS_VERSIONS_ROUTE,
} from '../../strings/api-consts'
import { DeviceEventResponse, DeviceEventType } from '../../types/DeviceEventTypes'
import { DeviceForensicsResponse, FileEventResponse } from '../../types/DeviceForensicTypes'
import {
  DashboardStats,
  DeviceAlertResponse,
  DeviceAlertRow,
  Devices,
  EmailUserCount,
  EnrolledDeviceInformation,
  InsecureDeviceInformation,
  Stats,
} from '../../types/DeviceTypes'
import { SelectOption } from '../../types/DropDownTypes'
import { CustomThunkDispatch } from '../../types/UserTypes'
import { reportErrorToDatadog } from '../../utils/error-logging'
import {
  setDeviceTableCurrentPage,
  setDashboardAlertsCreatedStat,
  setDashboardDevicesCreatedStat,
  setDashboardDeviceStats,
  setDashboardInsecureDevicesStat,
  setDashboardTotalScansStat,
  setDevices,
  setInsecureDevices,
  setTotalPages,
  setDeviceAlerts,
  setDeviceDetails,
  setDeviceEvents,
  setCurrentPageDeviceEvents,
  setTotalPagesDeviceEvents,
  setDeviceForensics,
  setIosSupportedVersions,
} from './devices.slice'

const server = new Server()

export const fetchDevices =
  (
    pageNumber: number,
    pageSize: number,
    severityFilter: SelectOption[],
    platform: string,
    searchTerm: string,
    sortOrder?: string,
    sortColumn?: string,
    osToShow?: string
  ) =>
  async (dispatch: CustomThunkDispatch) => {
    const platformRoute = () => {
      if (platform === 'enrolled') {
        return ENROLLED_DEVICE_ROUTE
      } else if (platform === 'pending') {
        return UNENROLLED_DEVICE_ROUTE
      }
    }

    let url = `${platformRoute()}?page=${pageNumber}&pageSize=${pageSize}`
    if (sortColumn) {
      url += `&sortColumn=${sortColumn}`
      if (sortOrder) {
        url += `&sortOrder=${sortOrder}`
      }
    }

    if (platform === 'pending') {
      url += '&enrolled=false'
    }

    if (searchTerm && searchTerm !== '') {
      const encodedSearchTerm = encodeURIComponent(searchTerm)
      url += `&name=${encodedSearchTerm}`
    }

    if (severityFilter.length > 0 && severityFilter.length !== 3) {
      const severityParams = severityFilter.map((severity) => `&severity=${severity.value}`).join('')
      url += severityParams
    } else if (severityFilter.length === 3) {
      url += ''
    }

    if (osToShow && osToShow !== 'all') {
      url += `&os=${osToShow}`
    }

    await server
      .get(url, dispatch)
      .then((deviceObject: Devices) => {
        dispatch(setDeviceTableCurrentPage(pageNumber - 1))
        dispatch(setTotalPages(deviceObject.totalPages))
        dispatch(setDevices(deviceObject.devices))
      })
      .catch((err) => {
        reportErrorToDatadog(err.message)
      })
  }

export const fetchDeviceDetails = (deviceId: string) => async (dispatch: CustomThunkDispatch) => {
  const url = API_URL + DEVICE_DETAILS_ROUTE

  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    },
    credentials: 'include',
    body: JSON.stringify({ deviceId: deviceId }),
  })
    .then((res) => {
      if (!res.ok) {
        return undefined
      }
      return res.json()
    })
    .then((deviceObject: EnrolledDeviceInformation) => {
      dispatch(setDeviceDetails(deviceObject))
      return deviceObject
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      dispatch(setDeviceDetails(undefined))
      return undefined
    })
}

export const fetchEventsForDevice =
  (
    pageNumber: number,
    pageSize: number,
    deviceId: string,
    types: DeviceEventType[],
    fromDateTime: string,
    toDateTime: string,
    searchFilter: string,
    sortColumn: string,
    sortOrder: string
  ) =>
  async (dispatch: CustomThunkDispatch) => {
    let url = `${DEVICE_EVENTS_ROUTE}?page=${pageNumber}&pageSize=${pageSize}&deviceId=${deviceId}`

    if (types.length > 0) {
      const typeParams = types.map((type) => `&eventType=${type}`).join('')
      url += typeParams
    }

    if (fromDateTime) {
      url += `&dateFrom=${fromDateTime}`
    }

    if (toDateTime) {
      url += `&dateTo=${toDateTime}`
    }

    if (searchFilter && searchFilter !== '') {
      const encodedSearchFilter = encodeURIComponent(searchFilter)
      url += `&search=${encodedSearchFilter}`
    }

    if (sortColumn) {
      url += `&sortColumn=${sortColumn}`
      if (sortOrder) {
        url += `&sortOrder=${sortOrder}`
      }
    }

    await server
      .get(url, dispatch)
      .then((data: DeviceEventResponse) => {
        dispatch(setDeviceEvents(data.events))
        dispatch(setTotalPagesDeviceEvents(data.totalPages))
        dispatch(setCurrentPageDeviceEvents(!pageNumber ? 0 : pageNumber - 1))
      })
      .catch((err) => {
        reportErrorToDatadog(err.message)
        dispatch(setDeviceEvents(undefined))
        dispatch(setTotalPagesDeviceEvents(1))
        dispatch(setCurrentPageDeviceEvents(0))
        console.error('Error fetching device events:', err)
        return undefined
      })
  }

export const fetchForensicsForDevice = (deviceId: string) => async (dispatch: CustomThunkDispatch) => {
  const url = `${DEVICE_FORENSICS_ROUTE}?deviceId=${deviceId}`
  await server
    .get(url, dispatch)
    .then((res: DeviceForensicsResponse) => {
      const data = {
        forensics: res.forensics,
        deviceId: deviceId,
      }
      dispatch(setDeviceForensics(data))
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      dispatch(
        setDeviceForensics({
          forensics: [],
          deviceId: deviceId,
        })
      )
      console.error('Error fetching device forensics:', err)
      return undefined
    })
}

export const fetchEventsForFile =
  (deviceId: string, filePath: string, pageNumber: number, pageSize: number) =>
  async (dispatch: CustomThunkDispatch) => {
    const url = `${DEVICE_FILE_EVENTS_ROUTE}?deviceId=${deviceId}&filePath=${encodeURIComponent(filePath)}&page=${pageNumber}&pageSize=${pageSize}`
    return await server
      .get(url, dispatch)
      .then((data: FileEventResponse) => {
        return data
      })
      .catch((err) => {
        reportErrorToDatadog(err.message)
        return []
      })
  }

export const fetchInsecureDevices = () => async (dispatch: CustomThunkDispatch) => {
  await server
    .get(INSECURE_DEVICE_ROUTE, dispatch)
    .then((deviceObject: InsecureDeviceInformation[]) => {
      dispatch(setInsecureDevices(deviceObject))
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
    })
}

export const emailMembers = (codes: string[]) => async (dispatch: CustomThunkDispatch) => {
  return server
    .post(EMAIL_USERS_ROUTE, codes, dispatch)
    .then((res) => {
      if (!res) {
        throw new Error('Response is undefined')
      }
      return res.json()
    })
    .then((count: EmailUserCount) => {
      return count.count
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      return undefined
    })
}

export const removeDevices = (codes: string[]) => async () => {
  const url = API_URL + UNENROLL_DEVICES_ROUTE

  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    },
    credentials: 'include',
    body: JSON.stringify(codes),
  })
    .then((res) => {
      return res.ok
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      return false
    })
}

export const fetchDashStats = () => async (dispatch: CustomThunkDispatch) => {
  server
    .get(DASHBOARD_STATS_ROUTE, dispatch)
    .then((stats: DashboardStats) => {
      dispatch(setDashboardDeviceStats(stats))
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
    })
}

export const fetchDevicesCreatedStats = () => async (dispatch: CustomThunkDispatch) => {
  const url = API_URL + DASHBOARD_DEVICES_CREATED_STAT_ROUTE

  fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    },
    credentials: 'include',
  })
    .then((res) => res.json())
    .then((stat: Stats) => {
      dispatch(setDashboardDevicesCreatedStat(stat))
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
    })
}

export const fetchAlertsCreatedStats = () => async (dispatch: CustomThunkDispatch) => {
  const url = API_URL + DASHBOARD_ALERTS_CREATED_STAT_ROUTE

  fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    },
    credentials: 'include',
  })
    .then((res) => res.json())
    .then((stat: Stats) => {
      dispatch(setDashboardAlertsCreatedStat(stat))
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
    })
}

export const fetchTotalScansStats = () => async (dispatch: CustomThunkDispatch) => {
  const url = API_URL + DASHBOARD_TOTAL_SCANS_STAT_ROUTE

  fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    },
    credentials: 'include',
  })
    .then((res) => res.json())
    .then((stat: Stats) => {
      dispatch(setDashboardTotalScansStat(stat))
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
    })
}

export const fetchInsecureDevicesStats = () => async (dispatch: CustomThunkDispatch) => {
  const url = API_URL + DASHBOARD_INSECURE_DEVICES_STAT_ROUTE

  fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    },
    credentials: 'include',
  })
    .then((res) => res.json())
    .then((stat: Stats) => {
      dispatch(setDashboardInsecureDevicesStat(stat))
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
    })
}

export const updateDeviceName =
  (accessCode: string, deviceName: string) =>
  async (dispatch: CustomThunkDispatch): Promise<boolean> => {
    const payload = {
      accessCode: accessCode,
      deviceName: deviceName,
    }

    return server
      .post(UPDATE_DEVICE_NAME_ROUTE, payload, dispatch)
      .then((res) => {
        return res?.ok ?? false
      })
      .catch((err) => {
        reportErrorToDatadog(err.message)
        return false
      })
  }

export const fetchAlertsForDevices =
  (deviceId: string, pageNumber: number, pageSize: number, sortOrder?: string, sortColumn?: string) =>
  async (dispatch: CustomThunkDispatch) => {
    let url = `${API_URL + DEVICE_ALERTS_ROUTE}?page=${pageNumber}&pageSize=${pageSize}`
    if (sortColumn) {
      url += `&sortColumn=${sortColumn}`
      if (sortOrder) {
        url += `&sortOrder=${sortOrder}`
      }
    }
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      },
      credentials: 'include',
      body: JSON.stringify({ deviceId: deviceId }),
    })
      .then((res) => res.json())
      .then((deviceObject: DeviceAlertResponse) => {
        dispatch(setDeviceAlerts(deviceObject.alerts))
      })
      .catch((err) => {
        reportErrorToDatadog(err.message)
        dispatch(setDeviceAlerts(undefined))
      })
  }

export const fetchScansForDevices =
  (deviceId: string, pageNumber: number, pageSize: number, sortOrder?: string, sortColumn?: string) => async () => {
    let url = `${API_URL + DEVICE_SCANS_ROUTE}?page=${pageNumber}&pageSize=${pageSize}`
    if (sortColumn) {
      url += `&sortColumn=${sortColumn}`
      if (sortOrder) {
        url += `&sortOrder=${sortOrder}`
      }
    }

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      },
      credentials: 'include',
      body: JSON.stringify({ deviceId: deviceId }),
    })
      .then((res) => res.json())
      .then((deviceObject: DeviceAlertRow[]) => {
        return deviceObject
      })
      .catch((err) => {
        reportErrorToDatadog(err.message)
        return undefined
      })
  }

export const fetchSupportedIosVersions = () => async (dispatch: CustomThunkDispatch) => {
  await server
    .get(SUPPORTED_IOS_VERSIONS_ROUTE, dispatch)
    .then((res: string[]) => {
      dispatch(setIosSupportedVersions(res))
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      dispatch(setIosSupportedVersions([]))
      console.error('Error fetching device forensics:', err)
    })
}
